import { Voucher } from '../types';
import { documentTypesInitialsEnum } from '../helpers';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import {
    getCurrencyDetails,
    getClientCurrencies,
    formatVoucherDate,
    buildRemittanceAmountsTable,
    coveredDocumentRef,
    convertCoveredAmountToCovering,
    documentPrimaryToLocalSource
} from './helpers';

/**
 * Build pdfInfo for Remittance / Payment Order
 *
 * Layout matches TravelBooster:
 * Page 1: Amount line, Transferred From (bank details), Transferred To (supplier/agent), Remarks
 * Page 2: Covered documents table (11-column)
 */
export function buildRemittancePdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const { code: currencyCode, symbol: currencySymbol } = getCurrencyDetails(info?.currency, context.currencies);
    const paymentObj = info?.paymentMethodObject || {};
    const paymentMethodType: string = info?.paymentMethodType || '';
    const amount = Number(info?.amount) || 0;
    const coveredDocuments = context.coveredDocuments;

    // ── Transferred From: bank/credit card details ───────────────────────────
    // For imported data, bank info is flat in paymentMethodObject (bankName, accountNumber)
    // For app-created, it's in remittanceTransferringBank nested object
    const transferringBank = paymentObj?.remittanceTransferringBank;
    const creditCardName = paymentObj?.creditCard?.creditCardName || '';

    let transferredFromName = '';
    let topLeftDetailsText = '';
    const pdfBodyBoxData: Array<{ label: string; value: string }> = [];

    if (paymentMethodType === 'Bank Transfer') {
        // Prefer fetched bank account (full data from DB), then remittanceTransferringBank, then flat paymentObj fields
        const bank = context.bankAccount;
        const bankName = bank?.bankName || transferringBank?.bankName || paymentObj?.bankName || info?.accountName || '';
        const bankBranch = bank?.branchName || transferringBank?.bankBranch || paymentObj?.bankBranch || '';
        const accountNumber = bank?.accountNumber || transferringBank?.accountNumber || paymentObj?.accountNumber || '';

        transferredFromName = bank?.accountName || transferringBank?.name || bankName;
        topLeftDetailsText = bankName;

        if (bankName) pdfBodyBoxData.push({ label: 'Bank', value: bankName });
        if (bankBranch) pdfBodyBoxData.push({ label: 'Branch', value: bankBranch });
        if (accountNumber) pdfBodyBoxData.push({ label: 'Account', value: accountNumber });
    } else if (paymentMethodType === 'Credit Card') {
        transferredFromName = creditCardName;
        topLeftDetailsText = creditCardName;
        if (creditCardName) pdfBodyBoxData.push({ label: 'Credit Card', value: creditCardName });
    } else {
        transferredFromName = transferringBank?.name || creditCardName || info?.accountName || '';
        topLeftDetailsText = transferredFromName;
    }

    // ── Transferred To: supplier or agent name ───────────────────────────────
    // Prefer context (fetched via getById) over stored info
    let transferredToName = '';
    if (context.supplierAccount) {
        transferredToName = (context.supplierAccount as any).supplierCompanyName || '';
    }
    if (!transferredToName && context.travelAgencyAccount) {
        transferredToName = (context.travelAgencyAccount as any).companyName || '';
    }
    if (!transferredToName) {
        transferredToName = info?.supplierName || info?.travelAgentName || '';
    }

    // ── pdfBodyBoxData2 (Transferred To details: bank, branch, account, IBAN, SWIFT, reference) ──
    const pdfBodyBoxData2: Array<{ label: string; value: string }> = [];
    const receivingBankName = typeof paymentObj?.receivingBank === 'object'
        ? paymentObj.receivingBank?.bankName || paymentObj.receivingBank?.name || ''
        : paymentObj?.receivingBank || '';
    if (receivingBankName) {
        pdfBodyBoxData2.push({ label: 'Bank', value: receivingBankName });
    }
    if (paymentObj?.branch) {
        pdfBodyBoxData2.push({ label: 'Branch', value: paymentObj.branch });
    }
    if (paymentObj?.account) {
        pdfBodyBoxData2.push({ label: 'Account', value: paymentObj.account });
    }
    if (paymentObj?.iban) {
        pdfBodyBoxData2.push({ label: 'IBAN', value: paymentObj.iban });
    }
    if (paymentObj?.swiftCode) {
        pdfBodyBoxData2.push({ label: 'SWIFT Code', value: paymentObj.swiftCode });
    }
    if (paymentObj?.reference) {
        pdfBodyBoxData2.push({ label: 'Reference', value: paymentObj.reference });
    }

    // ── Amounts table: Amount | Tax | Local Price | Rate | Primary Amount ─────
    // Replaces the old F.O.P/Description/Amount table, which showed a single
    // figure and no VAT — so a "Charge VAT" remittance printed nothing about the
    // tax it charges or what the payment is worth in ₪ / the reporting currency.
    //
    // The arithmetic lives in `buildRemittanceAmountsTable`, shared with the
    // inline copy in `Remittance.tsx`, so the PDF shown right after creation and
    // the one you get on reopen can no longer disagree.
    const { localCurrency, primaryCurrency } = getClientCurrencies(context.client, context.currencies);
    const vat = Number(info?.vatOnCommission) || 0;
    const docCurrencyId =
        info?.currency && typeof info.currency === 'object' ? info.currency._id : info?.currency;

    const {
        columns,
        rows,
        summaryLine,
        totalToPay,
        localRate: remittanceLocalRate,
        primaryToLocal: remittancePrimaryToLocal
    } = buildRemittanceAmountsTable({
        amount,
        vat,
        docCurrencyId,
        docCurrencyCode: currencyCode,
        docCurrencySymbol: currencySymbol,
        localCurrency,
        primaryCurrency,
        // A remittance INHERITS its rate from the document it covers, so an ILS
        // RMT settling a USD PRQ arrives carrying the PRQ's ₪-per-$ rate.
        // resolveDocumentLocalRate (inside the helper) pins a local-currency
        // document to 1 so that can't inflate the shekel total.
        storedRate: info?.localRate,
        // ...which is exactly why the ₪-per-$ rate has to be passed on its own:
        // on a ₪ remittance it lives in `info.rate`, and with only the pinned 1
        // to go on, Primary Amount used today's market rate (₪354 → $113.75
        // instead of $118 at the 3.00 the user set).
        storedPrimaryRate: documentPrimaryToLocalSource(info, localCurrency?._id, primaryCurrency?._id),
        amountLocal: info?.amountLocal,
        getConvertRate: context.getConvertRate
    });

    // ── Amount line text (TB style) ────────────────────────────────────────
    // Must state what actually leaves the bank: with "Charge VAT" the VAT sits
    // on top of the amount, so the transfer is amount + VAT.
    const amountLineText = `Please transfer from my account the amount of:  ${taytanFormatNumberToString(totalToPay)}  ${currencyCode}`;

    // ── Covered documents table (page 2) ─────────────────────────────────────
    let documentsColumns: PdfTablePropsColumn[] | null = null;
    let documentsRows: PdfTablePropsRow[] | null = null;
    let documentsSummaryLine: PdfTablePropsRow | null = null;

    if (coveredDocuments && coveredDocuments.length > 0) {
        documentsColumns = [
            { title: 'Doc#', fieldName: 'number', style: { width: '12%' } },
            { title: 'Value Date', fieldName: 'dueDate', style: { width: '10.5%' } },
            { title: 'Travel File #', fieldName: 'travelFileNumber', style: { width: '12%' } },
            { title: 'Service Name', fieldName: 'serviceName', style: { width: '13%' } },
            { title: 'Ref.', fieldName: 'ref', style: { width: '9.5%' } },
            { title: 'Amount', fieldName: 'amount', style: { width: '10%' } },
            { title: 'Comm.', fieldName: 'commission', style: { width: '6.5%' } },
            { title: 'VAT', fieldName: 'vat', style: { width: '6%' } },
            { title: 'Tax', fieldName: 'tax', style: { width: '6%' } },
            { title: 'Net Remit', fieldName: 'netToRemit', style: { width: '10%' } },
            { title: 'Curr.', fieldName: 'currency', style: { width: '4.5%' } }
        ];

        let totalAmount = 0;
        let totalCommission = 0;
        let totalVat = 0;
        let totalTax = 0;
        let totalNetToRemit = 0;

        documentsRows = coveredDocuments.map((doc) => {
            const docInfo = (Array.isArray(doc.info) ? doc.info[0] : doc.info) as any;
            const amt = Number(docInfo?.amount) || 0;
            const comm = Number(docInfo?.markup) || 0;
            const vat = Number(docInfo?.vatOnCommission) || 0;
            const tax = Number(docInfo?.tax) || 0;
            const netRemit = Number(docInfo?.netToRemit) || amt;

            // Rows stay in each document's own currency (the Curr. column says
            // which); the TOTAL line is in the remittance's, so convert.
            const toRemittance = (n: number) =>
                convertCoveredAmountToCovering({
                    amount: n,
                    docInfo,
                    coveringCurrencyId: docCurrencyId,
                    localId: localCurrency?._id,
                    primaryId: primaryCurrency?._id,
                    coveringLocalRate: remittanceLocalRate,
                    coveringPrimaryToLocal: remittancePrimaryToLocal,
                    getConvertRate: context.getConvertRate
                });
            totalAmount += toRemittance(amt);
            totalCommission += toRemittance(comm);
            totalVat += toRemittance(vat);
            totalTax += toRemittance(tax);
            totalNetToRemit += toRemittance(netRemit);

            const typeInitial = (documentTypesInitialsEnum as any)[doc.type] || '';
            const ref = coveredDocumentRef(docInfo);

            return {
                number: `${typeInitial}_${doc.number || ''}`,
                dueDate: formatVoucherDate(docInfo?.dueDate),
                travelFileNumber: docInfo?.travelFileNumber || '',
                serviceName: docInfo?.serviceName || '',
                ref,
                amount: taytanFormatNumberToString(amt),
                commission: taytanFormatNumberToString(comm),
                vat: taytanFormatNumberToString(vat),
                tax: taytanFormatNumberToString(tax),
                netToRemit: taytanFormatNumberToString(netRemit),
                currency: docInfo?.currency?.code || getCurrencyDetails(docInfo?.currency, context.currencies).code || ''
            };
        });

        documentsSummaryLine = {
            number: `TOTAL (${currencyCode})`,
            dueDate: '', travelFileNumber: '', serviceName: '', ref: '',
            amount: taytanFormatNumberToString(totalAmount) + (currencySymbol || ''),
            commission: taytanFormatNumberToString(totalCommission) + (currencySymbol || ''),
            vat: taytanFormatNumberToString(totalVat) + (currencySymbol || ''),
            tax: taytanFormatNumberToString(totalTax) + (currencySymbol || ''),
            netToRemit: taytanFormatNumberToString(totalNetToRemit) + (currencySymbol || ''),
            currency: currencyCode
        };
    }

    // Group covered docs by type: "PRQ: 4000088, 4000089 | VCH: 1000158"
    let linkedTo = '';
    if (coveredDocuments?.length) {
        const grouped: Record<string, string[]> = {};
        for (const doc of coveredDocuments) {
            const typeKey = (documentTypesInitialsEnum as any)[doc.type] || doc.type;
            if (!grouped[typeKey]) grouped[typeKey] = [];
            grouped[typeKey].push(String(doc.number || ''));
        }
        linkedTo = Object.entries(grouped).map(([type, nums]) => `${type}: ${nums.join(', ')}`).join('  |  ');
    }

    // For repay documents: include passenger (beneficiary) for address display
    const beneficiary = info?.beneficiary;
    const passenger = info?.isRepay
        ? (Array.isArray(beneficiary) ? beneficiary[0] : beneficiary) || info?.selectedPassengerFlatten || null
        : null;

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        topLeftDetailsText,
        bottomDetails: info?.for || '',
        linkedTo,
        amountLineText,
        transferredFrom: transferredFromName,
        transferredTo: transferredToName,
        passenger,
        pdfBodyBoxData,
        pdfBodyBoxData2,
        // Page 2: covered documents detail table
        documentsColumns,
        documentsRows,
        documentsSummaryLine
    };
}
